import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import IntlTelInput from 'react-intl-tel-input';
import 'react-intl-tel-input/dist/main.css';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { selectPortalGatewayISOCodesListToIgnoreAreaCode } from '../../store/selectors/settingsSelectors';
import { shouldIgnoreAreaCodeHelper } from '../../util/helpers/phoneNumberHelpers';
import { InputHeader, InputLabel } from './Components/InputComponents';
import { pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import Paragraph from '../Paragraph/Paragraph';

const getBorderColors = (isFocused, hasError) => {
  if (isFocused) {
    return themeColors.colorRegistration;
  }

  if (hasError) {
    return themeColors.colorError;
  }

  return themeColors.colorBorderPrimary;
};

const PhoneFieldWrap = styled.div`
  > .intl-tel-input {
    width: 100%;

    .country-list {
      width: 100%;
    }

    input {
      width: 100%;
      padding: 12px;
      border-radius: ${variables.borderRadius.borderRadius};
      color: ${themeColors.colorLabel};
      font-weight: 400;
      font-size: ${({ fontSize }) =>
        fontSize ?? variables.typography.p.small.fontSize};
      text-transform: ${({ hasSuggestion }) =>
        hasSuggestion ? 'capitalize' : 'none'};
      outline: none;
      border: 1px solid
        ${({ isFocused, hasError }) =>
          `${getBorderColors(isFocused, hasError)}}`};
    }

    .selected-flag {
      border-right: 1px solid
        ${({ isFocused, hasError }) =>
          `${getBorderColors(isFocused, hasError)}}`};
    }
  }
`;

const PhoneFieldError = styled.p`
  color: ${themeColors.colorError};
  font-size: ${pxToRem(14)};
  font-weight: 500;
  line-height: 1.35;
  margin: ${pxToRem(4)} 0;
`;

const MAX_PHONE_FAIL_ATTEMPTS = 10;

const PhoneField = ({
  setValue,
  name,
  label,
  defaultValue,
  isIraAccount,
  setError,
  error,
  optional,
  dataCy,
}) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [failCounter, setFailCounter] = useState(0);
  const selectedISOCodesListToIgnoreAreaCode = useSelector(
    selectPortalGatewayISOCodesListToIgnoreAreaCode,
  );
  const [errorMessage, setErrorMessage] = useState(error);

  useEffect(() => {
    setErrorMessage(error);
  }, [error]);

  // process number into string with area code for submission
  const processNumber = (isValid, phone, country) => {
    const shouldIgnoreAreaCode = shouldIgnoreAreaCodeHelper(
      country,
      selectedISOCodesListToIgnoreAreaCode,
    );

    if (shouldIgnoreAreaCode) {
      if (country.iso2 === 'pr') {
        return `+${country.dialCode}${phone}`;
      }

      const phoneNumber = `+${country.dialCode}${phone.substring(3)}`;
      return phoneNumber;
    }

    if (!phone) {
      return '';
    }

    if (phone.includes(`+${country.dialCode}`)) {
      return `${phone}`;
    }

    return `+${country.dialCode}${phone}`;
  };

  return (
    <PhoneFieldWrap
      isFocused={isFocused}
      hasError={errorMessage}
      data-cy={dataCy}
    >
      <InputHeader>{label && <InputLabel>{label}</InputLabel>}</InputHeader>
      <IntlTelInput
        onPhoneNumberFocus={() => {
          setIsFocused(true);
        }}
        onPhoneNumberBlur={(isValid, phone) => {
          setIsFocused(false);

          if (!isValid && phone) {
            if (failCounter >= MAX_PHONE_FAIL_ATTEMPTS) {
              setErrorMessage(t('quickRegistration.errors.multipleFails'));
            } else {
              setErrorMessage(t('quickRegistration.errors.phone'));
              setError(t('quickRegistration.errors.phone'));
              setFailCounter((prev) => prev + 1);
            }
          } else if (phone === '') {
            setErrorMessage(t('validationSchema.general.phoneNumberRequired'));
          } else {
            setErrorMessage('');
            setError('');
          }
        }}
        onPhoneNumberChange={(isValid, phone, country) => {
          setValue(name, processNumber(isValid, phone, country));
        }}
        fieldId={name}
        fieldName={name}
        defaultValue={defaultValue}
        onlyCountries={isIraAccount ? ['us'] : []}
        preferredCountries={isIraAccount ? [] : ['us']}
        defaultCountry="us"
      />
      {errorMessage && <PhoneFieldError>{errorMessage}</PhoneFieldError>}
      {optional && (
        <Paragraph
          marginTop={4}
          fontSize={13}
          color={themeColors.colorTextSecondary}
        >
          {t('quickRegistration.subText.phoneNumber')}
        </Paragraph>
      )}
    </PhoneFieldWrap>
  );
};

PhoneField.propTypes = {
  setValue: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  defaultValue: PropTypes.string,
  isIraAccount: PropTypes.bool,
  setError: PropTypes.func,
  optional: PropTypes.bool,
  dataCy: PropTypes.string,
  error: PropTypes.string,
};

export default PhoneField;
